import { useEffect, useRef, useState } from 'react';

/**
 * The current time (`Date.now()`), re-read on a timer so a component that
 * shows something time-based (a countdown, "ends in", "available until")
 * re-renders as the clock moves (ENG-832; shared with ENG-834).
 *
 * `intervalMs` is how long to wait before the next tick: a number, or a
 * function `(now) => ms` asked again before every tick, so a caller can tick
 * slowly while far from a moment and faster close to it, or land exactly on
 * a boundary. Options:
 *   - `enabled` (default true): false stops the timer (the value stays put).
 *   - `until`: a time in ms; the next tick never waits past it, so a
 *     countdown flips at its end rather than up to one interval late, and
 *     the timer stops once it has passed.
 *
 * One timer per hook, cleared on unmount and whenever `enabled` or `until`
 * changes. Uses plain `setTimeout`, so tests drive it with fake timers. When
 * a hidden tab becomes visible it ticks at once, since the browser may have
 * held its timers back meanwhile (ENG-876).
 */
export const useNow = (intervalMs = 60 * 1000, { enabled = true, until = null } = {}) => {
  const [now, setNow] = useState(() => Date.now());
  const intervalRef = useRef(intervalMs);
  intervalRef.current = intervalMs;

  useEffect(() => {
    if (!enabled) return undefined;
    let timer = null;
    const delayFrom = (current) => {
      const wanted = typeof intervalRef.current === 'function' ? intervalRef.current(current) : intervalRef.current;
      let delay = Number.isFinite(wanted) && wanted > 0 ? wanted : 60 * 1000;
      if (Number.isFinite(until)) delay = Math.min(delay, until - current);
      return delay;
    };
    const schedule = (current) => {
      // Past `until`, nothing more will change.
      if (Number.isFinite(until) && current >= until) return;
      timer = setTimeout(() => {
        const next = Date.now();
        setNow(next);
        schedule(next);
      }, Math.max(0, delayFrom(current)));
    };
    // Catch up at once: the value may be an interval old when `until` changes.
    const current = Date.now();
    setNow(current);
    schedule(current);
    // A background tab's timers are throttled (ENG-876): when it is shown
    // again, re-read the clock at once and start the timer afresh from it.
    const onVisible = () => {
      if (typeof document === 'undefined' || document.visibilityState === 'hidden') return;
      clearTimeout(timer);
      const shown = Date.now();
      setNow(shown);
      schedule(shown);
    };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, until]);

  return now;
};

export default useNow;
